<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { usePriorityTrafficStore } from '@/stores/priorityTraffic'
import { usePermissionsStore } from '@/stores/permissions'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import { PRIORITY_TRAFFIC_RESOURCES } from '@/types'
import FosButton from '@/components/ui/FosButton.vue'
import FosAlertDialog from '@/components/ui/FosAlertDialog.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'
import CoordinateInput from '@/components/ui/CoordinateInput.vue'
import { parseCoordinates, formatCoordinates } from '@/utils/coordinates'

const props = defineProps<{
  incidentId: string
  opId: string
}>()

const ptiStore = usePriorityTrafficStore()
const permissionsStore = usePermissionsStore()
const { opRole } = useCurrentOpRole()

const canResolve = computed(() => permissionsStore.can('priority_traffic.resolve', opRole.value))

// ─── Field state ──────────────────────────────────────────────────────────────

const locationAddress = ref('')
const locationCoordinates = ref('')
const notes = ref('')
const focusedField = ref<string | null>(null)

watch(
  () => ptiStore.activePti,
  (pti) => {
    if (!pti) return
    if (focusedField.value !== 'location_address') {
      locationAddress.value = pti.location_address ?? ''
    }
    if (focusedField.value !== 'location_coordinates') {
      locationCoordinates.value = pti.location_coordinates ?? ''
    }
    if (focusedField.value !== 'notes') {
      notes.value = pti.notes ?? ''
    }
  },
  { immediate: true, deep: true },
)

// ─── Timer ────────────────────────────────────────────────────────────────────

const elapsed = ref('0:00')
let timer: ReturnType<typeof setInterval> | null = null

watch(
  () => ptiStore.activePti?.declared_at,
  (declaredAt) => {
    if (timer) clearInterval(timer)
    timer = null
    if (declaredAt) {
      timer = setInterval(() => {
        const secs = Math.floor((Date.now() - declaredAt.toDate().getTime()) / 1000)
        elapsed.value = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`
      }, 1000)
    } else {
      elapsed.value = '0:00'
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  if (timer) clearInterval(timer)
})

// ─── Save handlers ────────────────────────────────────────────────────────────

const inputClass = 'w-full bg-surface-container-high text-on-surface font-mono text-sm px-3 py-2 border border-border rounded focus:outline-none focus:ring-1 focus:ring-amber-400/40 placeholder:text-outline/50'

function onBlur(field: 'location_address' | 'location_coordinates' | 'notes', value: string) {
  focusedField.value = null
  const pti = ptiStore.activePti
  if (!pti) return
  ptiStore.updatePtiField(props.incidentId, props.opId, pti.pti_id, field, value || null)
}

const ptiCoords = computed(() => parseCoordinates(locationCoordinates.value))

function onPtiCoordsUpdate(coords: { lat: number | null; lng: number | null }) {
  const formatted =
    coords.lat !== null && coords.lng !== null ? formatCoordinates(coords.lat, coords.lng) : ''
  locationCoordinates.value = formatted
  onBlur('location_coordinates', formatted)
}

function toggleResource(resource: string) {
  const pti = ptiStore.activePti
  if (!pti) return
  ptiStore.toggleResource(props.incidentId, props.opId, pti.pti_id, resource)
}

// ─── Resolve dialog ───────────────────────────────────────────────────────────

const showResolveDialog = ref(false)

async function confirmResolve() {
  const pti = ptiStore.activePti
  if (!pti) return
  await ptiStore.resolvePti(props.incidentId, props.opId, pti.pti_id)
  showResolveDialog.value = false
}
</script>

<template>
  <div
    v-if="ptiStore.activePti"
    class="flex flex-col border-l-2 border-amber-500 bg-surface overflow-y-auto"
  >
    <!-- Panel header -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-border bg-amber-500/10">
      <div class="flex items-center gap-2">
        <span class="w-2 h-2 rounded-full bg-amber-500 pti-dot-pulse shrink-0" />
        <span class="font-headline font-black text-xs uppercase tracking-widest text-amber-700 dark:text-amber-400">
          Priority Traffic — Team {{ ptiStore.activePti.declared_by_team_identifier }}
        </span>
      </div>
      <span class="font-mono text-lg font-bold text-amber-600 leading-none tabular-nums">{{ elapsed }}</span>
    </div>

    <!-- Form -->
    <div class="p-4 flex flex-col gap-3">
      <p class="font-mono text-[0.65rem] uppercase tracking-widest text-outline border-b border-border pb-2">
        Location
      </p>

      <div class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Address/Assignment</label>
        <input
          v-model="locationAddress"
          :class="inputClass"
          placeholder="Street address or landmark..."
          @focus="focusedField = 'location_address'"
          @blur="onBlur('location_address', locationAddress)"
        />
      </div>

      <CoordinateInput
        :lat="ptiCoords?.lat ?? null"
        :lng="ptiCoords?.lng ?? null"
        label="Coordinates"
        @update:coords="onPtiCoordsUpdate"
      />

      <p class="font-mono text-[0.65rem] uppercase tracking-widest text-outline border-b border-border pb-2 mt-1">
        Resources Requested
      </p>

      <div class="grid grid-cols-2 gap-1.5">
        <button
          v-for="resource in PRIORITY_TRAFFIC_RESOURCES"
          :key="resource"
          type="button"
          :class="[
            'px-2 py-1.5 font-mono text-xs border rounded transition-colors text-left',
            ptiStore.activePti.resources_requested.includes(resource)
              ? 'bg-amber-500 text-white border-amber-500'
              : 'bg-surface-container-high text-on-surface border-border hover:bg-surface-container-highest',
          ]"
          @click="toggleResource(resource)"
        >
          {{ resource }}
        </button>
      </div>

      <p class="font-mono text-[0.65rem] uppercase tracking-widest text-outline border-b border-border pb-2 mt-1">
        Notes
      </p>

      <div class="flex flex-col gap-1">
        <textarea
          v-model="notes"
          :class="[inputClass, 'resize-none']"
          rows="3"
          placeholder="Additional details..."
          @focus="focusedField = 'notes'"
          @blur="onBlur('notes', notes)"
        />
      </div>

      <div class="mt-2">
        <PermissionGuard action="priority_traffic.resolve" :role="opRole">
          <FosButton
            variant="secondary"
            class="w-full"
            @click="showResolveDialog = true"
          >
            Resolve Priority Traffic
          </FosButton>
        </PermissionGuard>
      </div>
    </div>
  </div>

  <FosAlertDialog
    :open="showResolveDialog"
    title="Resolve Priority Traffic"
    description="Mark this priority traffic incident as resolved. The alert will be dismissed."
    confirm-label="Resolve"
    @update:open="showResolveDialog = $event"
    @confirm="confirmResolve"
  />
</template>

<style scoped>
.pti-dot-pulse {
  animation: dot-pulse 1.2s ease-in-out infinite;
}

@keyframes dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.75); }
}
</style>
